// Every page frame (STYLE 4). Areas never render their own header: the
// route registry puts each route in the shell its path belongs to.
//   PublicShell      sign-in, sign-up, password, verify, join, legal pages
//   OnboardingShell  /welcome/*
//   AppShell         /projects*, /notifications, /settings/*, /p/:projectId/*: the sidebar
//                    and a slim top bar (Marius, side navigation option B); /org keeps the
//                    two-row header
//   PortalShell      /portal/* (the expert's own view)
//   FunderShell      /share/:token (read-only, no navigation)
//   CmShell          /cm/* (the Commission contract manager's view of one project)
//   GuestShell       /invite/:token*, /feedback/:token, /f/:token (no header)
//   KioskShell       /room/:token (check-in at the door)
//   PrintShell       /print/* (no frame)
import { createContext, Suspense, useContext, useEffect, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation, useMatch } from "react-router";
import { PAGES, pageLink, type Me } from "@convenor/shared";
import type { CmHeader } from "@convenor/shared/v3";
import { useMeta } from "../api/meta";
import { useMe } from "../api/session";
import { Menu } from "../ui/Menu";
import { useMeV3 } from "../areas/access/queries";
import { AssistantPanel } from "../areas/assistant/AssistantPanel";
import { FeedbackButton } from "../areas/feedback-button";
import { Loading } from "../ui/QueryStates";
import { Notice } from "../ui/Notice";
import { AppFooter, PublicFooter } from "./Footer";
import { AppHeader, AppTopBar, useShellProject } from "./Header";
import { SectionTabsProvider } from "./SectionTabs";
import { BreadcrumbsOff } from "../ui/PageHeader";
import { Sidebar } from "./Sidebar";
import { NoPortalWork } from "./NoPortalWork";
import { Wordmark } from "./Wordmark";
import { EuEmblem } from "../ui/EuEmblem";
import { DemoBanner } from "./DemoBanner";
import { TestMailBanner } from "./TestMailBanner";
import { TrialBanner } from "./TrialBanner";
import { useCurrentMe } from "./RequireAuth";

function SkipLink() {
  return (
    <a className="skip-link" href="#main">
      Skip to main content
    </a>
  );
}

/** Lazy pages load inside every shell. */
function Page({ children }: { children?: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children ?? <Outlet />}</Suspense>;
}

// ---------- public (sign-in etc.) ----------

export function PublicShell({ children, width = "narrow" }: { children?: ReactNode; width?: "narrow" | "form" | "wide" }) {
  const meta = useMeta();
  // The system has two narrow pages: a short form (640px; sign-in too) and a chooser (900px).
  const wide = width === "wide";
  return (
    <div className="shell">
      <SkipLink />
      <header className="plain-header plain-header--none">
        <div className="plain-header__inner">
          <Wordmark to={PAGES.signIn} />
          <span className="tagline">for EU projects</span>
          <div className="plain-header__right">
            <Menu label="English" look="quiet">
              {meta.languages.map((l) => (
                <li key={l.code}>
                  <button type="button" aria-current="true">
                    {l.label}
                  </button>
                </li>
              ))}
            </Menu>
            <a href={`mailto:${meta.supportEmail}`}>Help</a>
          </div>
        </div>
      </header>
      <main id="main" className={wide ? "page page--chooser" : "page page--form"} tabIndex={-1}>
        <Page>{children}</Page>
      </main>
      <PublicFooter />
    </div>
  );
}

// ---------- onboarding ----------

export function OnboardingShell() {
  const location = useLocation();
  // From step 3 (team) on, the header shows who and which organisation. The
  // first two steps are for people without an account: no session lookup there.
  const signedOutStep = /^\/welcome(\/account)?\/?$/.test(location.pathname);
  const me: Me | null = useMe({ enabled: !signedOutStep }).data ?? null;
  const showChips = me?.org && !signedOutStep && !/^\/welcome\/organisation\/?$/.test(location.pathname);
  return (
    <div className="shell">
      <SkipLink />
      <header className="plain-header">
        <div className="plain-header__inner">
          <Wordmark />
          {showChips ? (
            <div className="plain-header__right">
              <span className="user-chip">{me!.org!.name}</span>
              <span className="user-chip">{me!.user.name}</span>
            </div>
          ) : null}
        </div>
      </header>
      <main id="main" tabIndex={-1}>
        <div className="page">
          <Page />
        </div>
      </main>
      <AppFooter />
    </div>
  );
}

// ---------- app ----------

export function AppShell() {
  const me = useCurrentMe();
  // The same cached GET /api/me, typed as the v3 Me (which carries the trial).
  const meV3 = useMeV3().data;
  // page-links: not a link (reads the current address)
  const match = useMatch("/p/:projectId/*");
  const projectId = match?.params.projectId ?? null;
  const { pathname } = useLocation();
  const banners = (
    <>
      <TrialBanner trial={meV3?.org?.trial ?? null} />
      <DemoBanner />
      <TestMailBanner canSeeOutbox={meV3?.org?.role === "owner" || meV3?.org?.role === "admin"} />
    </>
  );
  // People and organisation (switched off for now) keeps its two-row header and its own areas.
  if (pathname === "/org" || pathname.startsWith("/org/")) {
    return (
      <div className="shell">
        <SkipLink />
        <AppHeader me={me} projectId={projectId} />
        <main id="main" tabIndex={-1}>
          <div className="page">
            {banners}
            <Page />
          </div>
        </main>
        <AppFooter />
      </div>
    );
  }
  return <SidebarShell me={me} projectId={projectId} banners={banners} />;
}

/** The sidebar, and beside it the slim top bar, the page and the footer. */
function SidebarShell({ me, projectId, banners }: { me: Me; projectId: string | null; banners: ReactNode }) {
  const { pathname } = useLocation();
  const { projects, current, draftName, homeProject } = useShellProject(projectId);
  // Outside a project (Settings, Alerts) the card shows the project the menu leads into, the last
  // one opened, rather than "Choose a project" (Edgar, 5 Nov 2026: the menu shifted).
  const shown = current ?? (draftName === null ? (projects.find((p) => p.id === homeProject) ?? null) : null);
  // The drawer on a phone: closed again on every page.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);
  return (
    <SectionTabsProvider>
      <BreadcrumbsOff.Provider value={true}>
        <div className="shell shell--sidebar">
          <SkipLink />
          <Sidebar homeProject={homeProject} current={shown} projects={projects} draftName={draftName} open={menuOpen} onClose={() => setMenuOpen(false)} />
          <div className="shell__body">
            <AppTopBar me={me} projectId={projectId} homeProject={homeProject} menuOpen={menuOpen} onMenu={() => setMenuOpen(true)} />
            <main id="main" tabIndex={-1}>
              <div className="page">
                {banners}
                <Page />
              </div>
            </main>
            <AppFooter />
          </div>
        </div>
      </BreadcrumbsOff.Provider>
    </SectionTabsProvider>
  );
}

// ---------- expert portal ----------

const PORTAL_NAV = [
  { to: PAGES.portal, label: "My work", end: true },
  { to: PAGES.portalHours, label: "Hours" },
  { to: PAGES.portalInvoices, label: "Invoices and claims" },
  { to: PAGES.portalContracts, label: "Contracts" },
];

export function PortalShell() {
  const me = useCurrentMe();
  const { pathname } = useLocation();
  // No project yet: only "My work" (a welcome) and Help; the other portal pages have nothing to show.
  const v3 = useMeV3();
  const noWork = !!v3.data && !v3.data.expertProfile;
  const nav = noWork ? PORTAL_NAV.filter((n) => n.end) : PORTAL_NAV;
  /** The support assistant for experts (docs/v3/ASSISTANT.md 1). */
  const [askOpen, setAskOpen] = useState(false);
  const current = (to: string, end?: boolean) => (end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`));
  return (
    <div className="shell">
      <SkipLink />
      <header className="topbar">
        <Wordmark to={PAGES.portal} />
        <nav className="topbar__nav" aria-label="Expert portal">
          {nav.map((n) => (
            <Link key={n.to} className="topbar__link" to={n.to} aria-current={current(n.to, n.end) ? "page" : undefined}>
              {n.label}
            </Link>
          ))}
        </nav>
        <span className="topbar__tools">
          <button type="button" className="btn btn--quiet" aria-expanded={askOpen} onClick={() => setAskOpen((v) => !v)}>
            Ask
          </button>
          <FeedbackButton />
          <Link
            className={pathname === PAGES.portalHelp ? "btn btn--secondary" : "btn btn--quiet"}
            to={PAGES.portalHelp}
            state={{ from: pathname }}
            aria-current={pathname === PAGES.portalHelp ? "page" : undefined}
          >
            Help
          </Link>
          <span className="topbar__org">{me.user.name}</span>
          <Link className="btn btn--quiet" to={PAGES.signOut}>
            Sign out
          </Link>
        </span>
      </header>
      {/* The page frame sits inside main, not on it: as a flex item of .shell with auto side
          margins, .page shrank to its widest content (a table) and the page scrolled sideways
          on a phone. As a block inside main it takes the screen's width. */}
      <main id="main" tabIndex={-1}>
        <div className="page">
          {/* IX-F12: the expert tester sees that the promised emails are not sent here either. */}
          <TestMailBanner canSeeOutbox={false} />
          {noWork && pathname !== PAGES.portalHelp ? <NoPortalWork name={me.user.name} /> : <Page />}
        </div>
      </main>
      <AppFooter />
      {askOpen ? <AssistantPanel audience="expert" onClose={() => setAskOpen(false)} /> : null}
    </div>
  );
}

// ---------- funder (share link) ----------

const ShellLabel = createContext<(label: string | null) => void>(() => {});

/** A page inside FunderShell names the share ("Programme manager, EU Delegation") for the header chip. */
export function useShellLabel(label: string | null | undefined): void {
  const set = useContext(ShellLabel);
  useEffect(() => {
    set(label ?? null);
    return () => set(null);
  }, [label, set]);
}

export function FunderShell() {
  const [label, setLabel] = useState<string | null>(null);
  return (
    <ShellLabel.Provider value={setLabel}>
      <div className="shell">
        <SkipLink />
        <header className="plain-header">
          <div className="plain-header__inner">
            <Wordmark />
            <span className="tagline">Read-only view</span>
            {label ? (
              <div className="plain-header__right">
                <span className="user-chip">{label}</span>
              </div>
            ) : null}
          </div>
        </header>
        <main id="main" tabIndex={-1}>
          <div className="page">
            <TestMailBanner canSeeOutbox={false} />
            <Page />
          </div>
        </main>
        <AppFooter />
      </div>
    </ShellLabel.Provider>
  );
}

// ---------- contract manager (/cm/*) ----------

const CM_TABS = [
  { page: "cmProject", label: "Overview" },
  { page: "cmTors", label: "Terms of Reference" },
  { page: "cmActivities", label: "Activities" },
  { page: "cmIndicators", label: "Indicators" },
  { page: "cmBudget", label: "Budget & experts" },
  { page: "cmMonthly", label: "Monthly reports" },
  { page: "cmReports", label: "Reports" },
  { page: "cmDocuments", label: "Documents" },
] as const;

const CmHeaderSet = createContext<(header: CmHeader | null) => void>(() => {});

/** A page inside CmShell passes the `header` of its answer: project name and the viewer chip. */
export function useCmShellHeader(header: CmHeader | null | undefined): void {
  const set = useContext(CmHeaderSet);
  useEffect(() => {
    if (header) set(header);
  }, [header, set]);
}

export function CmShell() {
  const [header, setHeader] = useState<CmHeader | null>(null);
  const { pathname } = useLocation();
  const projectId = useMatch("/cm/:projectId/*")?.params.projectId ?? null;
  const base = projectId ? pageLink("cmProject", { projectId }) : null;
  // A header left over from another project is not shown.
  const shown = header && header.projectId === projectId ? header : null;
  const current = (to: string) => (to === base ? pathname === base || pathname === `${base}/` : pathname === to || pathname.startsWith(`${to}/`));
  return (
    <CmHeaderSet.Provider value={setHeader}>
      <div className="shell">
        <SkipLink />
        <header className="plain-header">
          <div className="plain-header__inner">
            <Wordmark to={base ?? PAGES.cmStart} />
            <span className="tagline">{shown ? `${shown.project} · ${shown.preview ? "preview" : "read-only"}` : "Read-only view"}</span>
            <div className="plain-header__right">
              {shown ? <span className="user-chip">{shown.viewer}</span> : null}
              <Link to={PAGES.signOut}>Sign out</Link>
            </div>
          </div>
        </header>
        <main id="main" tabIndex={-1}>
          <div className="page">
            {projectId ? (
              <nav className="tabs" aria-label="Contract manager view">
                {CM_TABS.map((t) => {
                  const to = pageLink(t.page, { projectId });
                  return (
                    <Link key={t.page} className="tabs__tab" to={to} aria-current={current(to) ? "page" : undefined}>
                      {t.label}
                    </Link>
                  );
                })}
              </nav>
            ) : null}
            {/* CM-N3 / CO3 (retest 30 Oct): the project team's preview of this view. */}
            {shown?.preview ? (
              <Notice kind="info" title="This is what the contract manager sees">
                <p>
                  A preview: nothing can be decided, commented or uploaded here. <Link to={pageLink("funderView", { projectId: shown.projectId })}>Back to the contract manager settings</Link>
                </p>
              </Notice>
            ) : null}
            <TestMailBanner canSeeOutbox={false} />
            <Page />
          </div>
        </main>
        <AppFooter />
      </div>
    </CmHeaderSet.Provider>
  );
}

// ---------- guest (RSVP, feedback, public forms) ----------

/**
 * No header: one white card (max 640px) on the grid. The page draws the
 * banner, logo row, body and the card footer with the pieces below.
 */
export function GuestShell({ children }: { children?: ReactNode }) {
  return (
    <div className="shell">
      <SkipLink />
      <main id="main" className="guest-page" tabIndex={-1}>
        <Page>{children}</Page>
      </main>
    </div>
  );
}

/** The white guest card: banner (or dashed placeholder), logo row, body, footer. */
export function GuestCard({
  banner,
  logos,
  euStatement = false,
  footer,
  children,
  preview,
}: {
  banner?: ReactNode;
  logos?: ReactNode;
  euStatement?: boolean;
  footer?: ReactNode;
  children: ReactNode;
  /** Staff preview: the line above the card with the back link. */
  preview?: { backTo: string; backLabel: string };
}) {
  return (
    <>
      {preview ? (
        <p className="guest-preview">
          <span className="tag tag--info">Preview</span> This is what the guest sees ·{" "}
          <Link to={preview.backTo}>{preview.backLabel}</Link>
        </p>
      ) : null}
      <article className="guest-card">
        {/* Round 5 (EM8): no banner chosen, no box (the striped placeholder looked broken to guests). */}
        {banner ? <div className="guest-card__banner">{banner}</div> : null}
        {logos || euStatement ? (
          <div className="guest-card__logos">
            <div className="guest-card__logo-row">{logos}</div>
            {euStatement ? (
              <span className="guest-card__eu">
                <EuEmblem />
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="guest-card__body">{children}</div>
        {footer ? <footer className="guest-card__footer">{footer}</footer> : null}
      </article>
    </>
  );
}

// ---------- kiosk (check-in room) ----------

/** The room view frame: the page draws <KioskHeader> (event name, day, logos) and the rest. */
export function KioskShell() {
  return (
    <div className="shell shell--kiosk">
      <main id="main" className="kiosk-main" tabIndex={-1}>
        <Page />
      </main>
    </div>
  );
}

/** "Welcome to" · event title · "City · Day N · date", logos on the right. */
export function KioskHeader({ title, line, logos }: { title: ReactNode; line: ReactNode; logos?: ReactNode }) {
  return (
    <header className="kiosk-header">
      <div>
        <p className="kiosk-header__line">Welcome to</p>
        <p className="kiosk-header__title">{title}</p>
        <p className="kiosk-header__line">{line}</p>
      </div>
      <div className="kiosk-header__logos">{logos}</div>
    </header>
  );
}

// ---------- print ----------

/** No frame: the page draws the system's Print part (.print-page, its toolbar and sheet). */
export function PrintShell() {
  return (
    <main id="main">
      <Page />
    </main>
  );
}
